import { GridCol } from '@/components/nsw/grid/GridCol'
import { GridWrapper } from '@/components/nsw/grid/GridWrapper'
import { SideNav } from '@/components/nsw/side-nav/SideNav'
import { useCleanPathDefault } from '@/hooks/useCleanPathDefault'
import { Syllabus } from '@/kontent/content-types'
import { CommonContentTab } from '@/pages/learning-areas/[learningarea]/[syllabus]/[tab]'
import { CommonPageProps } from '@/types'
import {
	getLinkedItems,
	isRichtextElementEmpty,
	isRichtextEmpty,
} from '@/utils'
import { NextRouter, useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import InPageNav from '../InPageNav'
import RichText from '../RichText'
import { CommonCopyUrlWrapper } from '../ui/copy-to-clipboard/CommonCopyUrlWrapper'
import { SyllabusTabsTitle } from '../ui/syllabus-tabs/SyllabusTabsTitle'

export const overviewNavStyle = {
	'.nsw-side-nav__item': {
		borderLeft: '2px solid var(--nsw-brand-accent)',
	},
	'.nsw-side-nav__item:first-of-type': {
		borderTop: '0',
	},
} as const

export const Overview = ({
	mappings,
	data,
}: CommonPageProps<Syllabus, CommonContentTab<Syllabus>>) => {
	const router = useRouter()
	const { route, query } = router
	const {
		learningarea: paramLearningArea,
		syllabus: paramSyllabus,
		section,
	} = (router?.['components']?.[route] as NextRouter)?.query || query
	const { syllabus: syllabusResponse } = data
	const syllabus = syllabusResponse.item
	const [, setIsCourseFooterNodesSet] = useState(false)
	const currentUrl = useCleanPathDefault()

	const refCourseFooterNodes = useRef<string | JSX.Element | JSX.Element[]>(
		[],
	)

	const staticSideNavItems = [
		{
			text: 'Syllabus overview',
			href: `/learning-areas/${paramLearningArea}/${paramSyllabus}/overview`,
			isActive: !section,
			onClick: null,
			scroll: false,
		},
		{
			text: 'Course description',
			href: `/learning-areas/${paramLearningArea}/${paramSyllabus}/overview/course`,
			isActive: section == 'course',
			onClick: null,
			scroll: false,
		},
	]

	const overviewCourseItemsWithDisplayYes = getLinkedItems(
		syllabus.elements.courses,
		syllabusResponse.linkedItems,
	).filter((a) => a.elements.display_overview.value[0].name === 'Yes')

	const hideCourseTab =
		overviewCourseItemsWithDisplayYes.length == 0 &&
		isRichtextEmpty(syllabus.elements.course_content.value) &&
		isRichtextEmpty(syllabus.elements.course_content_footer.value)

	// Handle redirect on client-side only
	useEffect(() => {
		if (hideCourseTab && section == 'course') {
			router.push(
				`/404?path=/learning-areas/${paramLearningArea}/${paramSyllabus}/overview/course`,
			)
		}
	}, [hideCourseTab, section, router, paramLearningArea, paramSyllabus])

	// Render nothing if redirect is needed
	if (hideCourseTab && section == 'course') {
		// redirect to overview
	}

	const sideNavItems = getLinkedItems(
		syllabus.elements.additional_content,
		syllabusResponse.linkedItems,
	)

	const currentSectionTitle =
		section == 'course' || !section
			? staticSideNavItems.find((item) => item.isActive).text
			: sideNavItems?.find((item) => item.elements.slug.value === section)
					?.elements.title.value

	// Determine pageType based on current section
	const getPageType = () => {
		if (!section) {
			return 'Syllabus overview'
		} else if (section === 'course') {
			return 'Syllabus course'
		} else {
			return 'Syllabus page'
		}
	}

	const pageType = getPageType()

	// No section = index page
	const richTextContent = !section
		? syllabus.elements.web_content_rtb__content
		: section == 'course'
		? syllabus.elements.course_content
		: sideNavItems?.find((item) => {
				return item.elements.slug.value === section
		  })?.elements.content

	return (
		<div className="px-4 lg:px-0 pt-8">
			<SyllabusTabsTitle
				tabId="course-overview"
				mappings={mappings}
				syllabusCodename={syllabus.system.codename}
				className="relative !mb-0"
			/>
			<GridWrapper className="mt-0">
				<GridCol lg={3}>
					<SideNav
						css={overviewNavStyle}
						className="block"
						items={[
							...staticSideNavItems.filter((a) => {
								return hideCourseTab
									? a.text !== 'Course description'
									: true
							}),
							...sideNavItems.map((item) => {
								return {
									text: item.elements.title.value,
									href: `/learning-areas/${paramLearningArea}/${paramSyllabus}/overview/${item.elements.slug?.value}`,
									isActive:
										item.elements.slug.value === section,
									onClick: null,
									scroll: false,
								}
							}),
						]}
					/>
				</GridCol>
				<GridCol lg={9}>
					<div>
						<CommonCopyUrlWrapper url={currentUrl} className="mb-8">
							<h3>{currentSectionTitle}</h3>
						</CommonCopyUrlWrapper>
						<RichText
							currentPath={router.asPath}
							mappings={mappings}
							data-kontent-item-id={syllabus.system.id}
							data-kontent-element-codename="web_content_rtb__content"
							linkedItems={syllabusResponse.linkedItems}
							className="cms-content-formatting"
							richTextElement={richTextContent}
							suffixForHeading={syllabus.system.codename}
							renderFnBefore={(nodes) => {
								let combinedHeadings =
									section !== 'course'
										? (nodes as JSX.Element)
										: []

								if (section == 'course') {
									if (
										isRichtextElementEmpty(
											syllabus.elements.course_content,
										) &&
										isRichtextElementEmpty(
											syllabus.elements
												.course_content_footer,
										)
									) {
										// No course content, show empty navigation
										combinedHeadings = []
									} else {
										combinedHeadings = [
											nodes as JSX.Element[],
											refCourseFooterNodes.current,
										].flat()
									}
								}
								return (
									<InPageNav
										richTextElements={
											combinedHeadings as JSX.Element[]
										}
										className="!mt-0"
									/>
								)
							}}
							resolveCourseItems={
								overviewCourseItemsWithDisplayYes.length > 0 &&
								section == 'course'
									? overviewCourseItemsWithDisplayYes
									: null
							}
							pageType={pageType}
						/>
						{!isRichtextEmpty(
							syllabus.elements.course_content_footer.value,
						) &&
							section === 'course' && (
								<RichText
									className="mt-8 cms-content-formatting"
									richTextElement={
										syllabus.elements.course_content_footer
									}
									data-kontent-item-id={syllabus.system.id}
									data-kontent-element-codename="course_content_footer"
									linkedItems={syllabusResponse.linkedItems}
									mappings={mappings}
									renderFnBefore={(nodes) => {
										refCourseFooterNodes.current = nodes
										setIsCourseFooterNodesSet(true)
										return null
									}}
									pageType={pageType}
								/>
							)}
					</div>
				</GridCol>
			</GridWrapper>
		</div>
	)
}

export default Overview
